import { helpService } from '@/services/api'
import { useToastStore, useUserStore } from '@/stores/system'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Öffnet die Aaremo-Hilfe (hilfe.aaremo.de) in einem neuen Tab — AAR-1805.
 *
 * Bewusst ein Tab und kein Iframe-Drawer (AAR-1517): die Capacitor-WebView
 * teilt kein Cookie mit der Subdomain (AAR-1530) und der Drawer bräuchte
 * zusätzlich CSP-Arbeit an frame-ancestors.
 *
 * Zwei Einstiege teilen sich das hier: der Fragezeichen-Button in der Toolbar
 * (ab sm) und der Eintrag im Profilmenü (xs — dort ist die Toolbar voll).
 * Beide blenden sich über `canOpenHelp` aus — die Bedingung steht hier, damit
 * sie nicht an zwei Stellen auseinanderlaufen kann.
 */
export function useHelpPortal() {
	const { toastError } = useToastStore()
	const { isAdmin, isTeam } = storeToRefs(useUserStore())
	const loading = ref(false)

	/**
	 * Die Hilfe richtet sich an Verwalter: ihre Kapitel erklären Belegprüfung,
	 * Sync-Fehler und Administration. Für Eigentümer und Mieter ist davon nichts
	 * gedacht, deshalb sehen sie den Einstieg gar nicht erst.
	 *
	 * ACHTUNG, das ist nur der Sichtbarkeitsriegel. POST /v1/help/session steht
	 * auf beiden Stacks hinter `auth:api` allein und prüft KEINE Rolle — wer die
	 * Route kennt, bekommt weiterhin ein gültiges Token. Ein echter Riegel
	 * gehörte in HelpSessionController::issue() (L6 und L12).
	 */
	const canOpenHelp = computed(() => isAdmin.value || isTeam.value)

	/**
	 * Die API gibt immer die Startseite zurueck ({base}/?hs=<token>). Ein
	 * Zielpfad wird deshalb hier eingesetzt statt serverseitig: das nginx-Gate
	 * validiert `?hs=` per auth_request in `location /`, also auf JEDEM Pfad —
	 * `/aufgaben/handwerkerauftraege?hs=<token>` loest denselben Handoff aus.
	 * Der Pfad kommt aus dem eigenen Code, nie aus Nutzereingaben, damit hier
	 * kein offener Redirect entstehen kann.
	 */
	function withPath(url: string, path: string) {
		try {
			const target = new URL(url)
			target.pathname = path
			return target.toString()
		} catch {
			// Lieber die Startseite als gar keine Hilfe.
			return url
		}
	}

	async function openHelp(path?: string) {
		if (loading.value) return

		// Das Fenster muss SYNCHRON im Klick geöffnet werden. Ein window.open
		// nach dem await gilt dem Browser nicht mehr als Nutzergeste und wird
		// vom Popup-Blocker verworfen. Deshalb erst ein leerer Tab, dessen
		// Adresse später gesetzt wird.
		// Kein 'noopener' in den Features: das lässt window.open null
		// zurückgeben und nimmt uns genau den Handle, den wir brauchen.
		// Stattdessen wird opener selbst genullt, solange der Tab noch
		// about:blank und damit same-origin ist.
		const tab = window.open('', '_blank')
		if (tab) tab.opener = null

		loading.value = true
		try {
			const { data } = await helpService.session()
			// Das Token ist einmalig einlösbar und läuft nach 5 Minuten ab — die
			// URL wird deshalb nirgends gemerkt, jeder Klick holt ein frisches.
			const url = path ? withPath(data.url, path) : data.url
			if (tab) {
				tab.location.href = url
			} else {
				// Popup trotz Nutzergeste blockiert: dann eben im selben Tab.
				window.location.href = url
			}
		} catch {
			tab?.close()
			toastError({
				header: 'Hilfe nicht erreichbar',
				message:
					'Die Hilfe konnte nicht geöffnet werden. Bitte versuchen Sie es später erneut.',
			})
		} finally {
			loading.value = false
		}
	}

	return { openHelp, loading, canOpenHelp }
}
